import { useCallback } from 'react';
import { useKeycloak } from '@react-keycloak/web';

export interface DuplicateFileResult {
  isDuplicate: boolean;
  existingFileName?: string;
  existingDocumentId?: string;
  existingClientId?: string;
  existingClientName?: string;
  existingMatterId?: string;
  existingMatterNumber?: string;
  similarityScore?: number;
  message?: string;
}

export interface FileHashResult {
  hash: string;
  fileName: string;
  fileSize: number;
}

export const useDuplicateFileCheck = () => {
  const { keycloak } = useKeycloak();

  // Get JWT token from Keycloak
  const getAuthToken = () => {
    if (keycloak?.token) {
      return keycloak.token;
    }
    
    // Fall back to storage-based detection
    const jwtPattern = /^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/;
    
    for (const storage of [sessionStorage, localStorage]) {
      for (const key of Object.keys(storage)) {
        const value = storage.getItem(key);
        if (!value) continue;
        
        if (jwtPattern.test(value)) {
          return value;
        }
        
        if (value.startsWith('{')) {
          try {
            const parsed = JSON.parse(value);
            const tokenFields = ['token', 'access_token', 'accessToken', 'idToken', 'id_token'];
            for (const field of tokenFields) {
              if (parsed[field] && jwtPattern.test(parsed[field])) {
                return parsed[field];
              }
            }
          } catch (e) { /* ignore */ }
        }
      }
    }
    
    return '';
  };

  const calculateFileHash = useCallback(async (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const arrayBuffer = e.target?.result as ArrayBuffer;
          const hashBuffer = await crypto.subtle.digest('SHA-256', arrayBuffer);
          const hashArray = Array.from(new Uint8Array(hashBuffer));
          const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
          resolve(hashHex);
        } catch (error) {
          reject(error);
        }
      };
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsArrayBuffer(file);
    });
  }, []);

  const checkForDuplicate = useCallback(async (
    file: File,
    realmId: string
  ): Promise<DuplicateFileResult> => {
    try {
      // Calculate file hash
      const fileHash = await calculateFileHash(file);
      
      // Get auth token
      const token = getAuthToken();
      if (!token) {
        throw new Error('Authentication required');
      }

      // Check for duplicate in DMS
      const response = await fetch(`/api/v1/dms/documents/check-duplicate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          fileHash,
          fileName: file.name,
          fileSize: file.size,
          realmId
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      return result;

    } catch (error) {
      console.error('Error checking for duplicate file:', error);
      return {
        isDuplicate: false,
        message: 'Unable to check for duplicates at this time'
      };
    }
  }, [calculateFileHash]);

  const checkMultipleFiles = useCallback(async (
    files: File[],
    realmId: string
  ): Promise<Map<string, DuplicateFileResult>> => {
    const results = new Map<string, DuplicateFileResult>();
    
    // Check files in parallel for better performance
    const checkPromises = files.map(async (file) => {
      const result = await checkForDuplicate(file, realmId);
      return { fileName: file.name, result };
    });

    const checkResults = await Promise.allSettled(checkPromises);
    
    checkResults.forEach((promiseResult, index) => {
      const fileName = files[index].name;
      if (promiseResult.status === 'fulfilled') {
        results.set(fileName, promiseResult.value.result);
      } else {
        results.set(fileName, {
          isDuplicate: false,
          message: 'Error checking for duplicates'
        });
      }
    });

    return results;
  }, [checkForDuplicate]);

  return {
    checkForDuplicate,
    checkMultipleFiles
  };
}; 